/* ============================================================================
   Coffee Lava — motion
   Ported from new-site-design/src/lib/motion.ts.

   GSAP + ScrollTrigger are vendored into assets/ and loaded with `defer`
   before this file, so both are on `window` by the time this runs. No CDN,
   no module imports.

   Attribute contract (all `data-cl-` prefixed so nothing collides with
   Horizon's own scripts):

     [data-cl-reveal]           fade + rise into view
     [data-cl-reveal-group]     children reveal as one staggered batch
     [data-cl-parallax="0.1"]   vertical parallax, amount 0..1
     [data-cl-parallax-scope]   the scroll range a parallax child is measured against
     [data-cl-hero]             hero intro timeline
     [data-cl-split]            headline whose words lift in individually

   Everything no-ops under `prefers-reduced-motion: reduce`, exactly as the
   source does.
   ========================================================================= */
(function () {
  'use strict';

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;

  if (!gsap) return;

  var registered = false;

  /**
   * Horizon does not scroll the document. `base.css` sets
   * `html:has(.page-wrapper), html:has(.page-wrapper) body { overflow: hidden }`
   * and makes `.page-wrapper` the scroll container.
   *
   * ScrollTrigger listens to `window` by default, and window scroll events
   * never fire here — so every scrubbed animation would sit frozen at its
   * start value. Resolve the real scroller and hand it to ScrollTrigger.
   *
   * Falls back to `window` so the code still works on any layout (or a future
   * Horizon version) that scrolls the document normally.
   */
  function getScroller() {
    var wrapper = document.querySelector('.page-wrapper');
    if (!wrapper) return window;
    var overflowY = getComputedStyle(wrapper).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return wrapper;
    return window;
  }

  function initGsap() {
    if (!registered) {
      if (ScrollTrigger) {
        gsap.registerPlugin(ScrollTrigger);
        ScrollTrigger.config({ ignoreMobileResize: true });
        ScrollTrigger.defaults({ scroller: getScroller() });
      }
      gsap.defaults({ ease: 'power3.out' });
      registered = true;
    }
    return gsap;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Trigger positions are measured before lazy images have height, so anything
   * below the fold can end up anchored to the wrong scroll offset. Re-measure
   * as images settle.
   */
  function refreshOnLoad(root) {
    if (!ScrollTrigger) return function () {};
    var imgs = Array.prototype.slice
      .call(root.querySelectorAll('img'))
      .filter(function (i) {
        return !i.complete;
      });
    if (!imgs.length) return function () {};

    var pending = imgs.length;
    function done() {
      if (--pending <= 0) ScrollTrigger.refresh();
    }
    imgs.forEach(function (img) {
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });

    return function () {
      imgs.forEach(function (img) {
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
      });
    };
  }

  /**
   * Reveals every `[data-cl-reveal]` inside `root` on scroll.
   * Siblings sharing a `[data-cl-reveal-group]` parent stagger together.
   *
   * Entry is detected with IntersectionObserver rather than ScrollTrigger:
   * lazy images keep changing the document height as you scroll, and
   * pre-measured trigger offsets go stale against those shifts. The observer
   * reads live geometry, so a section can never be scrolled past unrevealed.
   * GSAP still owns the tween itself.
   */
  function revealAll(root) {
    var g = initGsap();
    var all = Array.prototype.slice.call(root.querySelectorAll('[data-cl-reveal]'));
    if (!all.length) return function () {};

    if (prefersReducedMotion()) {
      // Dropping the class is enough — it is what holds the hidden state.
      // Never clearProps here: elements carry inline custom properties
      // (e.g. --cl-accent on roast cards) that GSAP would wipe along with it.
      all.forEach(function (el) {
        el.classList.remove('cl-reveal');
      });
      return function () {};
    }

    root.classList.add('cl-reveal-ready');

    // Each observed element resolves to the batch it animates with: its
    // group's items, or just itself.
    var batchOf = new Map();
    var observed = [];

    Array.prototype.slice
      .call(root.querySelectorAll('[data-cl-reveal-group]'))
      .forEach(function (group) {
        var items = Array.prototype.slice.call(group.querySelectorAll('[data-cl-reveal]'));
        if (!items.length) return;
        batchOf.set(group, items);
        observed.push(group);
      });

    all.forEach(function (el) {
      if (el.closest('[data-cl-reveal-group]')) return;
      batchOf.set(el, [el]);
      observed.push(el);
    });

    function play(target) {
      var items = batchOf.get(target);
      if (!items) return;
      batchOf.delete(target);
      g.to(items, {
        opacity: 1,
        y: 0,
        duration: 0.95,
        stagger: items.length > 1 ? 0.09 : 0,
        ease: 'power3.out',
        overwrite: 'auto'
      });
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          play(entry.target);
        });
      },
      // Fire a little before the element reaches the fold.
      { rootMargin: '0px 0px -12% 0px', threshold: 0 }
    );

    observed.forEach(function (el) {
      io.observe(el);
    });

    return function () {
      io.disconnect();
      g.killTweensOf(all);
    };
  }

  /** Subtle vertical parallax for `[data-cl-parallax="0.2"]` elements. */
  function parallaxAll(root) {
    var g = initGsap();
    if (!ScrollTrigger || prefersReducedMotion()) return function () {};

    var ctx = g.context(function () {
      Array.prototype.slice.call(root.querySelectorAll('[data-cl-parallax]')).forEach(function (el) {
        var amount = parseFloat(el.getAttribute('data-cl-parallax') || '0.15');
        g.fromTo(
          el,
          { yPercent: -amount * 50 },
          {
            yPercent: amount * 50,
            ease: 'none',
            scrollTrigger: {
              trigger: el.closest('[data-cl-parallax-scope]') || el,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true
            }
          }
        );
      });
    }, root);

    return function () {
      ctx.revert();
    };
  }

  /** Splits text into per-word spans so lines can be masked and lifted in. */
  function splitWords(el) {
    if (el.getAttribute('data-cl-split-state') === 'done') {
      return Array.prototype.slice.call(el.querySelectorAll('.cl-word-inner'));
    }
    var lines = el.querySelectorAll('[data-cl-line]');
    var targets = [];
    var source = lines.length ? Array.prototype.slice.call(lines) : [el];

    source.forEach(function (line) {
      var text = line.textContent || '';
      line.textContent = '';
      text.split(/(\s+)/).forEach(function (chunk) {
        if (!chunk.trim()) {
          line.appendChild(document.createTextNode(chunk));
          return;
        }
        var outer = document.createElement('span');
        outer.className = 'cl-word';
        var inner = document.createElement('span');
        inner.className = 'cl-word-inner';
        inner.textContent = chunk;
        outer.appendChild(inner);
        line.appendChild(outer);
        targets.push(inner);
      });
    });

    el.setAttribute('data-cl-split-state', 'done');
    return targets;
  }

  /**
   * The hero intro. Lives here rather than in the section so the section stays
   * pure Liquid; the section just marks itself `[data-cl-hero]`.
   */
  function heroIntro(root) {
    var g = initGsap();
    if (prefersReducedMotion()) return function () {};

    var title = root.querySelector('[data-cl-split]');
    var bag = root.querySelector('[data-cl-hero-bag]');

    var ctx = g.context(function () {
      var tl = g.timeline({ defaults: { ease: 'expo.out' } });

      if (root.querySelector('[data-cl-hero-badge]')) {
        tl.from('[data-cl-hero-badge]', { opacity: 0, y: 14, duration: 0.8 });
      }
      if (title) {
        tl.from(splitWords(title), { yPercent: 115, duration: 1.25, stagger: 0.06 }, 0.08);
      }
      if (root.querySelector('[data-cl-hero-copy]')) {
        tl.from('[data-cl-hero-copy]', { opacity: 0, y: 18, duration: 0.9 }, 0.45);
      }
      if (root.querySelector('[data-cl-hero-cta]')) {
        tl.from('[data-cl-hero-cta]', { opacity: 0, y: 16, duration: 0.9 }, 0.6);
      }
      if (bag) {
        tl.from(bag, { opacity: 0, yPercent: 9, scale: 0.95, duration: 1.5, ease: 'power3.out' }, 0.5);
      }
      if (root.querySelector('[data-cl-hero-seal]')) {
        tl.from(
          '[data-cl-hero-seal]',
          { opacity: 0, scale: 0.8, duration: 1, ease: 'back.out(1.7)' },
          1
        );
      }
      if (root.querySelector('[data-cl-hero-btc]')) {
        tl.from(
          '[data-cl-hero-btc]',
          { opacity: 0, scale: 0.7, duration: 0.9, ease: 'back.out(1.4)' },
          1.15
        );
      }
      if (root.querySelector('[data-cl-bean]')) {
        tl.from('[data-cl-bean]', { opacity: 0, scale: 0.4, duration: 1.1, stagger: 0.05 }, 0.35);
      }

      // Each bean drifts on its own clock so the scatter never reads as a grid.
      g.utils.toArray('[data-cl-bean]').forEach(function (bean, i) {
        g.to(bean, {
          y: i % 2 ? 22 : -26,
          rotate: '+=' + (i % 3 ? 18 : -24),
          duration: 6 + (i % 5),
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
          delay: i * 0.24
        });
      });

      if (bag) {
        g.to(bag, { y: -14, duration: 3.8, ease: 'sine.inOut', repeat: -1, yoyo: true, delay: 1.8 });
      }
      // The coin rides the bag's bob, slightly out of phase so it reads as hung
      // off it rather than glued on.
      if (root.querySelector('[data-cl-hero-btc]')) {
        g.to('[data-cl-hero-btc]', {
          y: -10,
          duration: 4.2,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
          delay: 2.2
        });
      }

      if (root.querySelector('[data-cl-hero-topo]')) {
        g.to('[data-cl-hero-topo]', {
          rotate: 7,
          scale: 1.09,
          duration: 46,
          repeat: -1,
          yoyo: true,
          ease: 'none'
        });
      }
    }, root);

    return function () {
      ctx.revert();
    };
  }

  /* ── Wiring ─────────────────────────────────────────────────────────────── */

  var cleanups = [];

  function mount(root) {
    initGsap();
    cleanups.push(revealAll(root), parallaxAll(root), refreshOnLoad(root));
    Array.prototype.slice.call(root.querySelectorAll('[data-cl-hero]')).forEach(function (hero) {
      cleanups.push(heroIntro(hero));
    });
    if (ScrollTrigger) {
      requestAnimationFrame(function () {
        ScrollTrigger.refresh();
      });
    }
  }

  function unmount() {
    cleanups.forEach(function (fn) {
      if (typeof fn === 'function') fn();
    });
    cleanups = [];
  }

  /**
   * Re-apply a `#fragment` landing once the page has settled.
   *
   * The browser jumps to the target the moment it parses it — which here is
   * before the media above it has resolved its height. Loading `…#coffee`
   * measured a scroll of 149px, then the bands above grew and left the target
   * 477px below the fold: the visitor lands nowhere near the thing they
   * clicked. Repeat the scroll after load.
   *
   * Bails the moment the visitor scrolls for themselves, so a late `load`
   * event can never yank the page out from under them.
   */
  function settleHash() {
    var hash = window.location.hash;
    if (!hash || hash.length < 2) return;

    var target;
    try {
      target = document.querySelector(hash);
    } catch (e) {
      return; // not a usable selector (e.g. `#!` or an encoded fragment)
    }
    if (!target) return;

    var userMoved = false;
    var release = function () {
      userMoved = true;
    };
    var events = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
    events.forEach(function (type) {
      window.addEventListener(type, release, { passive: true });
    });

    /**
     * The offset is computed by hand rather than delegating to
     * `scrollIntoView`. Two reasons:
     *
     *   - `behavior` must be `'instant'`, not `'auto'`. `base.css` sets
     *     `scroll-behavior: smooth` on <html>, `'auto'` defers to that, and
     *     the animated scroll was then cancelled by ScrollTrigger's refresh.
     *   - `scrollIntoView` itself lands short here. With `overflow: clip` on
     *     one axis of <body> it consistently scrolled to 149px instead of the
     *     530px the element actually needed. Doing the arithmetic against the
     *     resolved scroller lands on the target every time.
     */
    var jump = function () {
      if (userMoved) return;
      var scroller = getScroller();
      var margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      var top = target.getBoundingClientRect().top;
      if (scroller === window) {
        window.scrollTo({ top: top + window.scrollY - margin, behavior: 'instant' });
      } else {
        var offset = top - scroller.getBoundingClientRect().top + scroller.scrollTop - margin;
        scroller.scrollTo({ top: offset, behavior: 'instant' });
      }
    };

    jump();
    requestAnimationFrame(jump);

    var done = function () {
      setTimeout(function () {
        if (ScrollTrigger) ScrollTrigger.refresh();
        jump();
        events.forEach(function (type) {
          window.removeEventListener(type, release);
        });
      }, 80);
    };

    if (document.readyState === 'complete') done();
    else window.addEventListener('load', done, { once: true });
  }

  function start() {
    mount(document.body);
    settleHash();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // The theme editor swaps section markup in place without a page load, so
  // remount against the new DOM whenever it does.
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!target) return;
    cleanups.push(revealAll(target), parallaxAll(target));
    if (target.matches && target.matches('[data-cl-hero]')) {
      cleanups.push(heroIntro(target));
    } else {
      Array.prototype.slice.call(target.querySelectorAll('[data-cl-hero]')).forEach(function (hero) {
        cleanups.push(heroIntro(hero));
      });
    }
    if (ScrollTrigger) ScrollTrigger.refresh();
  });

  document.addEventListener('shopify:section:unload', function () {
    if (ScrollTrigger) ScrollTrigger.refresh();
  });

  window.CoffeeLavaMotion = { mount: mount, unmount: unmount, splitWords: splitWords };
})();
